import { ClearOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { BarrierDirection, BarrierListFilters } from '@/types/barriers';

const TRISTATE_OPTIONS = [
  { value: 'any', labelKey: 'barriers.library.filters.tristate.any' },
  { value: 'yes', labelKey: 'barriers.library.filters.tristate.yes' },
  { value: 'no', labelKey: 'barriers.library.filters.tristate.no' },
] as const;

type Tristate = (typeof TRISTATE_OPTIONS)[number]['value'];

const DIRECTION_OPTIONS: readonly {
  value: BarrierDirection | 'any';
  labelKey: string;
}[] = [
  { value: 'any', labelKey: 'barriers.library.filters.direction.any' },
  { value: 'both', labelKey: 'barriers.direction.both' },
  { value: 'forward', labelKey: 'barriers.direction.forward' },
  { value: 'backward', labelKey: 'barriers.direction.backward' },
];

interface BarriersLibraryFiltersProps {
  readonly filters: BarrierListFilters;
  readonly onChange: (next: BarrierListFilters) => void;
  readonly onReset: () => void;
  readonly onRefresh: () => void;
  readonly disabled?: boolean;
}

export default function BarriersLibraryFilters({
  filters,
  onChange,
  onReset,
  onRefresh,
  disabled = false,
}: BarriersLibraryFiltersProps) {
  const { t } = useTranslation();

  const setRange = (range: [Dayjs | null, Dayjs | null] | null) => {
    onChange({
      ...filters,
      periodFrom: range?.[0] ? range[0].toISOString() : null,
      periodTo: range?.[1] ? range[1].toISOString() : null,
    });
  };

  const setRadius = (key: 'radiusMinM' | 'radiusMaxM', value: number | null) => {
    onChange({ ...filters, [key]: value });
  };

  const setDirection = (value: BarrierDirection | 'any') => {
    onChange({ ...filters, direction: value === 'any' ? null : value });
  };

  const setHasWaypoints = (value: Tristate) => {
    onChange({
      ...filters,
      hasWaypoints: value === 'any' ? null : value === 'yes',
    });
  };

  const setSearch = (search: string) => {
    onChange({ ...filters, search });
  };

  const tristateOptions = TRISTATE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey),
  }));

  const directionOptions = DIRECTION_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey),
  }));

  const rangeValue: [Dayjs, Dayjs] | undefined =
    filters.periodFrom && filters.periodTo
      ? [dayjs(filters.periodFrom), dayjs(filters.periodTo)]
      : undefined;

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Row gutter={[8, 8]} align="middle">
        <Col flex="auto">
          <Input.Search
            allowClear
            placeholder={t('barriers.library.filters.searchPlaceholder')}
            value={filters.search}
            onChange={(event) => setSearch(event.target.value)}
            disabled={disabled}
          />
        </Col>
        <Col>
          <Space size={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              disabled={disabled}
              title={t('barriers.library.filters.refresh')}
            />
            <Button
              icon={<ClearOutlined />}
              onClick={onReset}
              disabled={disabled}
              title={t('barriers.library.filters.reset')}
            />
          </Space>
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col span={24}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('barriers.library.filters.period')}
          </Typography.Text>
          <DatePicker.RangePicker
            value={rangeValue}
            onChange={setRange}
            disabled={disabled}
            style={{ width: '100%' }}
            allowEmpty={[true, true]}
          />
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('barriers.library.filters.radiusMin')}
          </Typography.Text>
          <InputNumber
            value={filters.radiusMinM ?? undefined}
            onChange={(value) => setRadius('radiusMinM', value as number | null)}
            min={0}
            step={50}
            disabled={disabled}
            style={{ width: '100%' }}
            addonAfter={t('common.units.meters')}
          />
        </Col>
        <Col span={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('barriers.library.filters.radiusMax')}
          </Typography.Text>
          <InputNumber
            value={filters.radiusMaxM ?? undefined}
            onChange={(value) => setRadius('radiusMaxM', value as number | null)}
            min={0}
            step={50}
            disabled={disabled}
            style={{ width: '100%' }}
            addonAfter={t('common.units.meters')}
          />
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('barriers.library.filters.direction.label')}
          </Typography.Text>
          <Select
            value={filters.direction ?? 'any'}
            onChange={setDirection}
            options={directionOptions}
            disabled={disabled}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('barriers.library.filters.hasWaypoints')}
          </Typography.Text>
          <Select
            value={tristateValue(filters.hasWaypoints)}
            onChange={setHasWaypoints}
            options={tristateOptions}
            disabled={disabled}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
    </Space>
  );
}

function tristateValue(value: boolean | null): Tristate {
  if (value === null) return 'any';
  return value ? 'yes' : 'no';
}
